import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { circeWithText, iconStyle } from "../styles/styles";
import { checkCoordinateIsValid, getPosition} from "../helpers/helpers";
import {
  defaultTextStyles,
  defaultCircleStyles,
  defaultImageStyles,
} from "../styles/default.object.styles";
import * as _ from 'lodash'
import {getVectorContext} from 'ol/render';
import animate from '../helpers/animate';

export class TrackedObjectFeature {
  constructor(
    map,
    imageWidthInPixels,
    imageHeightInPixels,
    trackedObject,
    type,
    userDefinedStyles,
    mainMapProps
  ) {
    this.map = map;
    return new Promise(async (resolve, reject) => {
      if (
        !imageWidthInPixels ||
        !imageHeightInPixels ||
        !trackedObject ||
        !type ||
        !trackedObject.attributes
      ) {
        reject('Not enought params');
      }

      this.mapSizesInPixel = {
        imageWidthInPixels,
        imageHeightInPixels,
      };

      this.type = type;
      this.trackedObject = trackedObject;

      const { kx, ky } = trackedObject.attributes;

      if (!checkCoordinateIsValid(parseFloat(kx)) || 
      !checkCoordinateIsValid(parseFloat(ky))) {
        reject("wrong coordinates");
      }
      this.mainMapProps = mainMapProps
      const {mapProps} = this.mainMapProps;
   
      this.coordinates = getPosition({
        kx, 
        ky, 
        mapProps, 
        extent: 
        this.map.extentMap
      }, this.map.modes.globalMap)

      this.renderCallback = [];
      this.openLayersFeature = new Feature({
        type,
        payload: this,
        geometry: new Point(this.coordinates),
      });
      this.isSelected = false;

      this.openLayersFeature.setId(trackedObject.id);
      const featureStyle = await this.getObjectStyle(
        trackedObject,
        userDefinedStyles
      );
      this.openLayersFeature.setStyle(featureStyle);
      resolve(this.openLayersFeature);
    });
  }

  async animate(
    {kx: fromKx, ky: fromKy }, 
    { kx: toKx, ky: toKy }, geometry, map_, renderCallbackArray){
    const mainLayer = this.map.mainLayer;
    const map = this.map.map;
    const {mapProps} = this.mainMapProps;
    const extent = this.map.extentMap;
    const globMapMode =  map_.modes.globalMap;
    const pointsArray = [];

    let animFrameId = 0;
    const steps = 60;
    let xDist = (toKx - fromKx);
    let yDist = (toKy - fromKy);

    for (let i = 1; i <= steps; i++) {
      const stepDelta = animate('easeInOutSine', i / steps);
      const newX = fromKx + stepDelta * xDist;
      const newY = fromKy + stepDelta * yDist;
      pointsArray.push({kx: newX, ky: newY})
    };

     await new Promise((resolve) => {
      const renderCallback = async(eventData) => {
        if(animFrameId >= pointsArray.length) {
          mainLayer.un('postrender', renderCallback);
          resolve();
          return
        }
        const vectorContext = getVectorContext(eventData);
        const currentPoint = pointsArray[animFrameId];
        const coords = getPosition({
          kx: currentPoint.kx, 
          ky: currentPoint.ky, 
          mapProps, 
          extent
        }, globMapMode);
        geometry.setCoordinates(coords);
        vectorContext.drawGeometry(geometry);
        animFrameId += 1;
        map.render();
      }
      renderCallbackArray.push(renderCallback);

      mainLayer.on('postrender', renderCallback.bind(this));
      map.render();
    });
  }

  async setSelectObejctStyle(color) {
    this.isSelected = true;
    const style = await this.getObjectStyle(this.trackedObject, {
      circle: {
        stroke: {
          color,
        },
      },
    });
    this.openLayersFeature.setStyle(style);
  }

  async resetStyles() {
    this.isSelected = false;
    const style = await this.getObjectStyle(this.trackedObject, {
      circle: {
        stroke: {
          color: "#505050",
        },
      },
    });
    this.openLayersFeature.setStyle(style);
  }

  checkAttributesDiff(oldObject, newObject) {
    const checkTitle = oldObject.title === newObject.title;
    const checkImage = oldObject.file_url === newObject.file_url;

    return !checkTitle || !checkImage;
  }

  async addTrackLineIfNeeded(oldCoordinates, newCoordinates) {
    // call method add linestring on map
    const isDrawTrackOn = this.map.modes.drawTracks;
    if (!isDrawTrackOn) {
      if (this.trackLine) {
        this.trackLine = undefined;
      }
      return;
    }
    if (!this.trackLine) {
      const colorProps = this.trackedObject?.attributes?.properties?.filter((prop) => {
        return prop.key === 'color'
      });
      const colorProp = colorProps ? colorProps[0] : {}
      this.trackLine = await this.map.addTrackLineOnSource(this.trackedObject.id, [
        oldCoordinates,
        newCoordinates,
      ], colorProp.value);
      return
    }
    const trackLineClass = this.map.getFeaturePayload(this.trackLine);

    await trackLineClass.updateTrackLine(newCoordinates);
  }

  async updateTrackedObject(newTrackedObject) {
    const geometry = this.openLayersFeature.getGeometry();

    const { kx, ky } = newTrackedObject.attributes;

    if (!checkCoordinateIsValid(kx) || !checkCoordinateIsValid(ky)) {
      return null;
    }

    const {mapProps} = this.mainMapProps
 
    this.coordinates = getPosition({
      kx, ky, mapProps,       
      extent: this.map.extentMap
    }, this.map.modes.globalMap)

    if (
      this.checkAttributesDiff(
        this.trackedObject.attributes,
        newTrackedObject.attributes
      )
    ) {
      const featureStyle = await this.getObjectStyle(newTrackedObject);
      this.openLayersFeature.setStyle(featureStyle);
    }
    
    const { kx: oldKx, ky: oldKy } = this.trackedObject.attributes;
    if(this.renderCallback.length > 0) {
      this.renderCallback.forEach((cb) => this.map.mainLayer.un('postrender', cb));
      this.renderCallback = [];
    }
    if(this.map.isAnimate){
      this.animate({kx: oldKx, ky: oldKy }, { kx, ky }, geometry, this.map, this.renderCallback);
    }
    this.trackedObject = newTrackedObject;
    if(!this.map.isAnimate){
      geometry.setCoordinates(this.coordinates);
    }
    await this.addTrackLineIfNeeded(geometry.getCoordinates(), this.coordinates);
  }

  updateCoord(){
    const { mapProps } = this.mainMapProps;
    
    const { kx, ky } = this.trackedObject.attributes
    if (!checkCoordinateIsValid(kx) || !checkCoordinateIsValid(ky)) {
      return;
    }

    this.coordinates = getPosition({
      kx, 
      ky, 
      mapProps, 
      extent: this.map.extentMap
    }, this.map.modes.globalMap);
    const geometry = this.openLayersFeature.getGeometry();
    geometry.setCoordinates(this.coordinates);
  }

  async getObjectStyle(object, userDefinedStyles) {
    const { title, file_url, properties } = object.attributes;
    let objColor
    if(properties){
      const objColorProp = properties.filter((prop) => {
        return prop.key === 'objectColor'
      })[0];
      objColor = objColorProp?.value;
    }
    
    if (file_url) {
      const asyncImageLoader = () => {
        return new Promise((resolve, reject) => {
          const img = new Image(30, 30);
          img.onerror = reject;
          img.onload = () => resolve(img);
          img.src = file_url;
        });
      };

      const loadedImage = await asyncImageLoader();
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = loadedImage.width;
      canvas.height = loadedImage.height;
      context.drawImage(
        loadedImage,
        0,
        0,
        loadedImage.width,
        loadedImage.height
      );

      const iconStyles = _.merge(
        {},
        {
          text: defaultTextStyles(title),
          image: defaultImageStyles(canvas, loadedImage),
        },
        userDefinedStyles
      );
      this.currentStyles = iconStyles;

      return iconStyle(iconStyles);
    } else {
      const circleStyles = _.merge(
        {},
        { text: defaultTextStyles(title), circle: defaultCircleStyles },
        userDefinedStyles
      );


      if(objColor){
        circleStyles.circle.fill.color = objColor;
      }
      
      this.currentStyles = circleStyles;

      return circeWithText(circleStyles);
    }
  }
}
